import { useQuery } from '@powersync/react';
import { createContext, useContext, useMemo, type ReactNode } from 'react';

import { type PaywallPlan } from '@/components/domain/PlanOptionCard';
import { useAuth } from '@/lib/auth';

/**
 * Master switch for the paid tier. Disabled for the private beta so nobody
 * hits limits, paywalls, or upgrade prompts. Flip to `true` (and wire the
 * RevenueCat seam below) to bring billing back — every gate keys off this.
 */
export const BILLING_ENABLED = false;

// Free-tier limits (the design's gate: 2 checklists, 10 runs).
export const FREE_TEMPLATE_LIMIT = 2;
export const FREE_RUN_LIMIT = 10;

export const PLANS: PaywallPlan[] = [
  {
    id: 'annual',
    title: 'Annual',
    price: '$39.99 /yr',
    sub: '$3.33 / month',
    badge: 'Save 33%',
  },
  { id: 'monthly', title: 'Monthly', price: '$4.99 /mo', sub: 'Billed monthly' },
];

export const TRIAL_LINE = '7 days free, then $39.99/year. Cancel anytime in Settings.';

/**
 * Entitlement + purchase surface.
 *
 * RevenueCat integration seam — to go live:
 *   1. `pnpm --filter @rbf/mobile add react-native-purchases` (needs a dev/EAS
 *      build; not Expo Go or web).
 *   2. Set EXPO_PUBLIC_REVENUECAT_KEY and configure products in App Store
 *      Connect / Play Console mapped to the `pro` entitlement.
 *   3. In BillingProvider: `Purchases.configure({ apiKey })`, read
 *      `getCustomerInfo()` → `entitlements.active['pro']` for `isPro`, and wire
 *      `purchase`/`restore` to `purchasePackage` / `restorePurchases`.
 * Until then the app is free-tier: `isPro` is false and purchase/restore throw
 * a friendly BillingUnavailable error the paywall surfaces.
 */
export class BillingUnavailableError extends Error {
  constructor() {
    super('Subscriptions are not available in this build yet.');
    this.name = 'BillingUnavailableError';
  }
}

type BillingContext = {
  isPro: boolean;
  loading: boolean;
  purchase: (planId: string) => Promise<void>;
  restore: () => Promise<void>;
};

const Ctx = createContext<BillingContext>({
  isPro: false,
  loading: false,
  purchase: async () => {
    throw new BillingUnavailableError();
  },
  restore: async () => {
    throw new BillingUnavailableError();
  },
});

export function BillingProvider({ children }: { children: ReactNode }) {
  // Default free-tier. Replace with RevenueCat customer-info state (see above).
  const value = useMemo<BillingContext>(
    () => ({
      isPro: false,
      loading: false,
      purchase: async () => {
        throw new BillingUnavailableError();
      },
      restore: async () => {
        throw new BillingUnavailableError();
      },
    }),
    [],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useEntitlement = () => useContext(Ctx);

export type Usage = {
  templates: number;
  runs: number;
  atTemplateLimit: boolean;
  atRunLimit: boolean;
};

/** Reactive free-tier usage counts + whether each limit is reached. */
export function useUsage(): Usage {
  const { session } = useAuth();
  const { isPro } = useEntitlement();
  const uid = session?.user.id ?? '';

  const { data: t } = useQuery<{ n: number }>(
    'SELECT COUNT(*) AS n FROM templates WHERE owner_id = ? AND deleted_at IS NULL',
    [uid],
  );
  const { data: r } = useQuery<{ n: number }>(
    'SELECT COUNT(*) AS n FROM instances WHERE owner_id = ?',
    [uid],
  );

  const templates = t[0]?.n ?? 0;
  const runs = r[0]?.n ?? 0;
  return {
    templates,
    runs,
    atTemplateLimit: BILLING_ENABLED && !isPro && templates >= FREE_TEMPLATE_LIMIT,
    atRunLimit: BILLING_ENABLED && !isPro && runs >= FREE_RUN_LIMIT,
  };
}
